// @flow

import React, { type Node, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { useIncomingPayment, getCustomerName, getCountry } from '@tectonics/prs/incoming-payment';
import { useUserInputAsTextField, useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUniqueId } from '@tectonics/hooks/use-unique-id';
import { useUserInputValidationError } from '@tectonics/prs/validation/use-user-input-validation-result';
import { type UserInputValidationError } from '@tectonics/prs/validation/types';
import { serverErrorMessage } from '@tectonics/prs/validation/server-error-message';
import { validateCardHolderName as cardHolderNameContainsError } from '@tectonics/prs/card/validation/card-holder-name';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { translate } from '@tectonics/translate';

import { TextField } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';

export function FieldCardHolderName(): Node {
  const incomingPayment = useIncomingPayment();
  const incomingCustomerName = getCustomerName(incomingPayment);
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [value, handleChange] = useUserInputAsTextField('cardHolderName');
  const [, setCardHolderName] = useUserInputValue('cardHolderName');

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardHolderName');
  const country = getCountry(incomingPayment);

  const mustShow = useMemo(() => (
    !incomingCustomerName
    || cardHolderNameContainsError(incomingCustomerName)
    || country === 'mx'
    || country === 'ar'
    || country === 'py'
    || country === 'pa'
    || country === 'cr'
    || country === 'sv'
    || country === 'gt'
    || country === 'do'
    || country === 'ke'
    || country === 'ng'
    || country === 'tz'
    || country === 'ug'
    || country === 'zm'
    || country === 'eg'
    || country === 'ec'
    || country === 'in'
    || country === 'jm'
    || country === 'tr'
  ), [incomingCustomerName, country]);

  useEffect(() => {
    if (!mustShow) {
      setCardHolderName(incomingCustomerName);
    }
  }, [incomingCustomerName, setCardHolderName, mustShow]);

  if (!mustShow)
    return null;

  return (
    <Root data-test-name="card-holder-name">
      <label htmlFor={inputId}>
        <LabelMessage />
      </label>
      <TextField
        id={inputId}
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        data-secret
        name="card-holder-name"
      />
      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
    </Root>
  );
}

function LabelMessage(): string {
  return translate('volcanes.fieldCardHolderName');
}

const Root = styled.div``;

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch (validationError.id) {
    case 'empty':
      return translate('volcanes.validation-error.fieldCardHolderNameMessageErrors.emptyMessage');

    case 'contains-number':
      return translate('volcanes.validation-error.fieldCardHolderNameMessageErrors.containsNumberMessage');

    case 'max-length':
      return translate('volcanes.validation-error.fieldCardHolderNameMessageErrors.maxLengthMessage');

    default:
      return translate('volcanes.validation-error.fieldCardHolderNameMessageErrors.defaultMessage');
  }
};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError) || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}
